/**
 * Blog HTML content helpers — preserve formatting between editor, API, and reader.
 */
import DOMPurify from 'dompurify';

const BLOG_PURIFY_CONFIG = {
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'del', 'strike',
    'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'blockquote',
    'code', 'pre', 'img', 'span', 'mark', 'sub', 'sup', 'hr', 'div', 'iframe',
  ],
  ALLOWED_ATTR: [
    'href', 'target', 'rel', 'class', 'src', 'alt', 'title',
    'style', 'data-placeholder', 'data-align', 'data-width', 'data-youtube-video',
    'width', 'height', 'allowfullscreen', 'allow', 'frameborder',
  ],
  ALLOW_DATA_ATTR: false,
  ADD_ATTR: ['target', 'rel'],
};

/** Normalize HTML from API before loading into TipTap. */
export function prepareHtmlForEditor(html: string): string {
  const trimmed = html?.trim() ?? '';
  if (!trimmed) {
    return '<p></p>';
  }
  // Collapse whitespace-only text nodes that span a line break between tags
  // (typical of pretty-printed/indented HTML, e.g. multi-line <ul> markup).
  // Left alone, TipTap's DOM parser turns these stray whitespace nodes into
  // phantom empty list items/paragraphs. Only whitespace containing a
  // newline is collapsed, so a meaningful single space between inline
  // elements on the same line (e.g. "<em>fast</em> <em>food</em>") survives.
  return trimmed.replace(/>[ \t]*\n\s*</g, '><');
}

/** Sanitize blog HTML for safe rendering (keeps links, spacing, basic styling). */
export function sanitizeBlogHtml(html: string): string {
  if (!html?.trim()) {
    return '';
  }
  return DOMPurify.sanitize(html, BLOG_PURIFY_CONFIG);
}
